import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangeFilterProps {
  dateRange: string;
  onDateRangeChange: (range: string) => void;
  customStartDate?: Date;
  customEndDate?: Date;
  onCustomDateChange?: (start: Date | undefined, end: Date | undefined) => void;
}

export function DateRangeFilter({
  dateRange,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomDateChange,
}: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState<Date | undefined>(customStartDate);
  const [tempEnd, setTempEnd] = useState<Date | undefined>(customEndDate);

  const presets = [
    { label: '7 Days', value: '7days' },
    { label: '30 Days', value: '30days' },
    { label: '90 Days', value: '90days' },
  ];

  const handleApplyCustom = () => {
    if (tempStart && tempEnd && onCustomDateChange) {
      onCustomDateChange(tempStart, tempEnd);
      onDateRangeChange('custom');
      setIsOpen(false);
    }
  };

  const handlePresetClick = (value: string) => {
    onDateRangeChange(value);
    if (onCustomDateChange) {
      onCustomDateChange(undefined, undefined);
    }
  };

  const getDisplayLabel = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`;
    }
    return null;
  };

  return (
    <div className="flex items-center gap-2">
      {/* Preset buttons */}
      {presets.map((preset) => (
        <button
          key={preset.value}
          onClick={() => handlePresetClick(preset.value)}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            dateRange === preset.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-foreground border border-border hover:bg-accent'
          )}
        >
          {preset.label}
        </button>
      ))}

      {/* Custom date picker */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={dateRange === 'custom' ? 'default' : 'outline'}
            className={cn(
              'gap-2 min-w-[140px]',
              dateRange === 'custom' && 'bg-primary text-primary-foreground'
            )}
          >
            <CalendarIcon className="h-4 w-4" />
            {getDisplayLabel() || 'Custom'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Start Date</p>
              <Calendar
                mode="single"
                selected={tempStart}
                onSelect={setTempStart}
                disabled={(date) => date > new Date() || (tempEnd ? date > tempEnd : false)}
                initialFocus
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">End Date</p>
              <Calendar
                mode="single"
                selected={tempEnd}
                onSelect={setTempEnd}
                disabled={(date) => date > new Date() || (tempStart ? date < tempStart : false)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleApplyCustom}
                disabled={!tempStart || !tempEnd}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
